import { Entity } from "../../Entity";
import PropertyFactory from "../../Factories/PropertyFactory";
import { FactoryInterface } from "../../FactoryInterface";
import Image from "../../../Image/Image";
import { PropertyInterface } from "./PropertyInterface";
import { UnitInterface } from "../Unit/UnitInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { AddressInterface } from "../../../Interface/AddressInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import { SubscriptionInterface } from "../Subscription/SubscriptionInterface";
import { AgencyFeeInterface } from "../AgencyFee/AgencyFeeInterface";
import SubscriptionTypeContainer from "../../../Entity/Entities/Subscription/SubscriptionType/SubscriptionTypeContainer";
import Translator from "../../../Translation/Translator";
import { ChargeTemplateInterface } from "../Charge/ChargeTemplateInterface";
import Store from "../../../Store/Store";

class Property extends Entity implements PropertyInterface {

  constructor(public data) {
    super();
  }

  get nameOfEntity() {
    return Translator.trans("property");
  }

  get nameForUrl() {
    return "property";
  }

  get labelForEntity() {
    return "label-property"
  }

  public getFactory(): FactoryInterface {
    return PropertyFactory;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get id(): number {
    return this.data.id;
  }

  get avatar(): string {
    return this.data.avatar;
  }

  get displayName(): string {
    return this.data.displayName || this.address.formattedAddress;
  }

  get complexName(): string {
    return this.data.complexName;
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get contactId(): number {
    return this.data.contactId;
  }

  get company(): CompanyInterface | undefined {
    return this.relation("app.companies", this.data.companyId)[0];
  }

  get associationOwner(): CompanyInterface | undefined {
    return this.relation("app.companies", this.data.associationOwnerId)[0];
  }

  get contact(): PersonInterface {
    return this.relation("app.persons", this.data.contactId)[0];
  }

  get unitIds(): number[] {
    return this.data.unitIds;
  }

  get units(): UnitInterface[] {
    return Object.keys(Store.getConfigurator().getState().units?.byId || {}).map((id) => Store.getConfigurator().getState().units?.byId?.[id])
            .filter(unit => unit?.propertyId === this.id);
  }

  get updatedAt(): Date {
    return this.data.updatedAt;
  }

  get subscription(): SubscriptionInterface {
    return this.subscriptionRelation(SubscriptionTypeContainer.pickIdFromLabel("Properties"), this.data.id);
  }

  get isSubscribed(): boolean {
    return this.subscription !== null;
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get address(): AddressInterface {
    return this.data.address;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get note(): string {
    return this.data.note;
  }

  get contactInformationOfAssociation(): string {
    return this.data.contactInformationOfAssociation;
  }

  get associationOwnerId(): number {
    return this.data.associationOwnerId;
  }


  get managerId(): number {
    return this.data.managerId;
  }

  get manager(): PersonInterface {
    return this.relation("app.persons", this.data.managerId)[0];
  }

  get agencyBankAccount(): number {
    return this.data.agencyBankAccount;
  }

  get defaultAgencyFees(): AgencyFeeInterface[] {
    return this.all("app.agencyFees")
      .filter((agencyFee: AgencyFeeInterface) =>
                                    agencyFee.removed === false &&
                                    agencyFee.objectType === AgencyFeeContainer.pickIdFromLabel("Property") &&
                                    agencyFee.objectId === this.data.id);
  }

  get inheritedAgencyFee(): AgencyFeeInterface | null {
    if (this.company && this.company.defaultAgencyFees.length > 0) {
      return this.company.defaultAgencyFees[0];
    }

    if (this.company && this.company.agency && this.company.agency.defaultAgencyFees.length > 0) {
      return this.company.agency.defaultAgencyFees[0];
    }

    return null;
  }

  public renderAvatar(params: any = {}): any {
    return Image.getAvatarAbsolutely(this.data.avatar, "property", params);
  }

  get chargeTemplates(): ChargeTemplateInterface[] {
    // We have to return non-default charge templates.
    return this.all("app.chargeTemplates")
      .filter((chargeTemplate: ChargeTemplateInterface) => !!chargeTemplate &&
                                                           chargeTemplate.removed === false &&
                                                           chargeTemplate.isDefault === false &&
                                                           chargeTemplate.propertyId === this.data.id)
      .sort((template1: ChargeTemplateInterface, template2: ChargeTemplateInterface) => {
        if (template1.creationDate > template2.creationDate) {
          return 1;
        }

        if (template1.creationDate < template2.creationDate) {
          return -1;
        }

        return  0;
      });
  }

  get isReadonly(): boolean {
    return this.removed;
  }
}

export { Property };
